import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Trash2, Send } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import Contractor1099Header from '../components/pm/Contractor1099Header';
import ArchitectHeader from '../components/pm/ArchitectHeader';
import EngineerHeader from '../components/pm/EngineerHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import ContractorProjectPicker from '../components/contractor/ContractorProjectPicker';
import { useAuth } from '../utils/AuthContext';
import { isDesignerRole, isInternal1099ContractorUser } from '../utils/userRoles';
import { useCRM } from '../utils/CRMContext';
import { useContractorTimesheetAccess } from '../hooks/useContractorTimesheetAccess';
import { useContractorPayablesAccess } from '../hooks/useContractorPayablesAccess';
import {
  buildContractorProjectOptions,
  CONTRACTOR_OTHER_PROJECT_ID,
  contractorProjectEntryLabel,
} from '../utils/contractorProjectPicker';
import { buildContractorPayablesPath } from '../utils/notificationDeepLinks';
import {
  deleteContractorDraftEntry,
  endOfContractorWeek,
  formatWeekRange,
  formatContractorSubmittedAt,
  isCurrentContractorWeek,
  subscribeContractorProfile,
  saveContractorDraftEntry,
  startOfContractorWeek,
  submitContractorTimesheet,
  subscribeContractorTimesheets,
  subscribeContractorSubmittedEntries,
  subscribeContractorWeekEntries,
  formatCallableError,
  type SubmitContractorTimesheetResult,
} from '../utils/contractorTimesheetService';
import { hasContractorPriorFiling, isContractorW9OnFile } from '../utils/contractorW9';
import {
  buildDayBreakdownByTimesheet,
  buildJobBreakdownByTimesheet,
  formatContractorBreakdownHours,
  resolveTimesheetJobs,
} from '../utils/contractorTimesheetBreakdown';
import {
  CONTRACTOR_MAX_DAILY_HOURS,
  formatContractorDailyCapError,
  remainingDailyMinutes,
  sumDraftMinutesForDate,
  wouldExceedContractorDailyCap,
} from '../utils/contractorDailyHours';
import {
  CONTRACTOR_DAY_ENTRY_MINUTES,
  contractorRequiresDayRateInvoicing,
  defaultSubmitBillingFromProfile,
  draftDayLogExists,
  formatContractorEntryQuantity,
  inclusiveWorkDateKeys,
  profileRateConfiguredForSubmit,
  totalBillableDays,
} from '../utils/contractorInvoiceBilling';
import { parseContractorPortalPreview } from '../utils/contractorPortalPreview';
import ContractorProfileEditor from '../components/contractor/ContractorProfileEditor';
import '../components/admin/UserManagement.css';
import { isCrmTestUser } from '../utils/userRoles';
import { canPreviewPartnerPortals } from '../utils/userRoles';
import type {
  ContractorProfile,
  ContractorTimeEntry,
  ContractorTimeEntryStatus,
  ContractorTimesheet,
  ContractorTimesheetStatus,
} from '../types';
import '../components/pm/pm-shared.css';
import './ContractorTimesheetsPage.css';

function formatHours(minutes: number): string {
  return (minutes / 60).toFixed(2);
}

const ENTRY_STATUS_LABELS: Record<ContractorTimeEntryStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  invoiced: 'Invoiced',
  paid: 'Paid',
};

const TIMESHEET_STATUS_LABELS: Record<ContractorTimesheetStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  invoiced: 'Invoiced',
  paid: 'Paid',
};

function weekEntriesSummary(
  entries: ContractorTimeEntry[],
  weekTimesheet: ContractorTimesheet | undefined,
  draftMinutes: number,
  logsByDay: boolean,
): { label: string; statusClass: string } {
  const totalMinutes = entries.reduce((s, e) => s + e.minutes, 0);
  const draftDays = totalBillableDays(entries.filter((e) => e.status === 'draft'));
  const status = weekTimesheet?.status;
  if (logsByDay) {
    if (status && status !== 'draft') {
      const days = totalBillableDays(entries);
      return {
        label: `${days} day${days === 1 ? '' : 's'} ${TIMESHEET_STATUS_LABELS[status].toLowerCase()}`,
        statusClass: `ct-entries-total ct-entries-total--${status}`,
      };
    }
    if (draftDays === 0 && entries.length > 0) {
      const dominant = entries[0]?.status ?? 'invoiced';
      const days = totalBillableDays(entries);
      return {
        label: `${days} day${days === 1 ? '' : 's'} ${ENTRY_STATUS_LABELS[dominant].toLowerCase()}`,
        statusClass: `ct-entries-total ct-entries-total--${dominant}`,
      };
    }
    return {
      label: `${draftDays} day${draftDays === 1 ? '' : 's'} draft`,
      statusClass: 'ct-entries-total',
    };
  }
  if (status && status !== 'draft') {
    const mins = weekTimesheet.totalMinutes || totalMinutes;
    return {
      label: `${formatHours(mins)}h ${TIMESHEET_STATUS_LABELS[status].toLowerCase()}`,
      statusClass: `ct-entries-total ct-entries-total--${status}`,
    };
  }
  if (totalMinutes > 0 && draftMinutes === 0) {
    const dominant = entries[0]?.status ?? 'invoiced';
    return {
      label: `${formatHours(totalMinutes)}h ${ENTRY_STATUS_LABELS[dominant].toLowerCase()}`,
      statusClass: `ct-entries-total ct-entries-total--${dominant}`,
    };
  }
  return {
    label: `${formatHours(draftMinutes)}h draft`,
    statusClass: 'ct-entries-total',
  };
}

function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseHoursInput(raw: string): number {
  const h = parseFloat(raw);
  if (!Number.isFinite(h) || h <= 0) return 0;
  return Math.round(h * 60);
}

const ContractorTimesheetsPage: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers, timberProjects, enhancedDeals, leads } = useCRM();
  const { canAccess, canOnBehalf } = useContractorTimesheetAccess();
  const { canAccess: canViewPayables, canManage: canManageContractorProfile } = useContractorPayablesAccess();
  const [searchParams] = useSearchParams();
  const portalPreview = useMemo(() => {
    if (!canPreviewPartnerPortals(user?.role)) return null;
    return parseContractorPortalPreview(searchParams.toString());
  }, [searchParams, user?.role]);

  const TimesheetHeader = isInternal1099ContractorUser({
    role: user?.role,
    is1099Contractor: user?.is1099Contractor,
  })
    ? Contractor1099Header
    : isDesignerRole(user?.role)
      ? ArchitectHeader
      : user?.role === 'engineer'
        ? EngineerHeader
        : AppHeader;

  const contractorOptions = useMemo(
    () =>
      crmUsers
        .filter((u) => u.isActive !== false && u.is1099Contractor)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [crmUsers],
  );

  const [selectedContractorId, setSelectedContractorId] = useState('');
  const [weekStart, setWeekStart] = useState(() => startOfContractorWeek(new Date()));
  const [entries, setEntries] = useState<ContractorTimeEntry[]>([]);
  const [timesheets, setTimesheets] = useState<ContractorTimesheet[]>([]);
  const [submittedEntries, setSubmittedEntries] = useState<ContractorTimeEntry[]>([]);
  const [profile, setProfile] = useState<ContractorProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [projectId, setProjectId] = useState('');
  const [workDate, setWorkDate] = useState(toDateInput(new Date()));
  const [workDateFrom, setWorkDateFrom] = useState(toDateInput(new Date()));
  const [workDateTo, setWorkDateTo] = useState(toDateInput(new Date()));
  const [hours, setHours] = useState('1');
  const [description, setDescription] = useState('');
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<SubmitContractorTimesheetResult | null>(null);
  const [submitRate, setSubmitRate] = useState('');

  const weekEnd = useMemo(() => endOfContractorWeek(weekStart), [weekStart]);
  const weekRangeLabel = useMemo(() => formatWeekRange(weekStart), [weekStart]);
  const isSelectedCurrentWeek = useMemo(() => isCurrentContractorWeek(weekStart), [weekStart]);
  const targetUserId = canOnBehalf && selectedContractorId ? selectedContractorId : user?.id || '';

  useEffect(() => {
    if (portalPreview?.uid && canOnBehalf) {
      setSelectedContractorId(portalPreview.uid);
      return;
    }
    if (!canOnBehalf) {
      if (user?.id) setSelectedContractorId(user.id);
      return;
    }
    setSelectedContractorId((prev) => {
      if (contractorOptions.length === 0) return prev;
      if (contractorOptions.some((u) => u.uid === prev)) return prev;
      return contractorOptions[0].uid;
    });
  }, [canOnBehalf, user?.id, contractorOptions, portalPreview?.uid]);

  useEffect(() => {
    setAttest(false);
  }, [targetUserId]);

  const submitBillingDefaults = useMemo(
    () => defaultSubmitBillingFromProfile(profile),
    [profile],
  );

  useEffect(() => {
    const { rate } = submitBillingDefaults;
    setSubmitRate(rate > 0 ? String(rate) : '');
  }, [targetUserId, submitBillingDefaults.billingUnit, submitBillingDefaults.rate]);

  useEffect(() => {
    if (!targetUserId) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    const unsub = subscribeContractorProfile(
      targetUserId,
      (next) => {
        setProfile(next);
        setProfileLoading(false);
      },
      () => {
        setProfile(null);
        setProfileLoading(false);
      },
    );
    return unsub;
  }, [targetUserId]);

  useEffect(() => {
    if (!targetUserId) return;
    const unsub = subscribeContractorWeekEntries(targetUserId, weekStart, weekEnd, setEntries);
    return unsub;
  }, [targetUserId, weekStart, weekEnd]);

  useEffect(() => {
    if (!targetUserId) return;
    const unsub = subscribeContractorTimesheets(targetUserId, setTimesheets);
    return unsub;
  }, [targetUserId]);

  useEffect(() => {
    if (!targetUserId) return;
    const unsub = subscribeContractorSubmittedEntries(targetUserId, setSubmittedEntries);
    return unsub;
  }, [targetUserId]);

  const dayBreakdownByTimesheet = useMemo(
    () => buildDayBreakdownByTimesheet(submittedEntries),
    [submittedEntries],
  );

  const jobBreakdownByTimesheet = useMemo(
    () => buildJobBreakdownByTimesheet(submittedEntries),
    [submittedEntries],
  );

  const projectOptions = useMemo(
    () =>
      buildContractorProjectOptions(
        timberProjects,
        enhancedDeals,
        profile?.allowedProjectIds,
        leads,
      ),
    [timberProjects, enhancedDeals, profile?.allowedProjectIds, leads],
  );

  const recentProjectIds = useMemo(() => {
    const ids: string[] = [];
    for (const e of entries) {
      if (e.projectId && !ids.includes(e.projectId)) ids.push(e.projectId);
    }
    return ids.slice(0, 5);
  }, [entries]);

  const draftEntries = useMemo(() => entries.filter((e) => e.status === 'draft'), [entries]);
  const weekDraftMinutes = draftEntries.reduce((s, e) => s + e.minutes, 0);

  const remainingMinutesForSelectedDay = useMemo(
    () => remainingDailyMinutes(draftEntries, workDate),
    [draftEntries, workDate],
  );

  const maxHoursForSelectedDay = useMemo(
    () => Math.max(0.25, remainingMinutesForSelectedDay / 60),
    [remainingMinutesForSelectedDay],
  );

  const weekTimesheet = useMemo(() => {
    const startMs = weekStart.getTime();
    const endMs = weekEnd.getTime();
    return timesheets.find((t) => {
      const ps = t.periodStart.getTime();
      const pe = t.periodEnd.getTime();
      return ps === startMs || (ps <= startMs && pe >= endMs);
    });
  }, [timesheets, weekStart, weekEnd]);

  const logsByDay = contractorRequiresDayRateInvoicing(profile);

  const entriesSummary = useMemo(
    () => weekEntriesSummary(entries, weekTimesheet, weekDraftMinutes, logsByDay),
    [entries, weekTimesheet, weekDraftMinutes, logsByDay],
  );

  const weekLocked = weekTimesheet && weekTimesheet.status !== 'draft';

  const addEntry = async () => {
    if (!user?.id || !targetUserId || !projectId || !description.trim()) {
      setError('Project and work description are required.');
      return;
    }
    let minutes: number;
    if (logsByDay) {
      minutes = CONTRACTOR_DAY_ENTRY_MINUTES;
      const weekMin = toDateInput(weekStart);
      const weekMax = toDateInput(weekEnd);
      const dateKeys = inclusiveWorkDateKeys(workDateFrom, workDateTo).filter(
        (k) => k >= weekMin && k <= weekMax,
      );
      if (dateKeys.length === 0) {
        setError('Choose a from/to date range within the selected pay week.');
        return;
      }
      const toCreate = dateKeys.filter(
        (k) => !draftDayLogExists(draftEntries, projectId, new Date(`${k}T12:00:00`)),
      );
      if (toCreate.length === 0) {
        setError('Every date in that range is already logged for this project.');
        return;
      }
      if (weekLocked) {
        setError(`${weekRangeLabel} has already been submitted.`);
        return;
      }

      setBusy(true);
      setError(null);
      try {
        for (const k of toCreate) {
          await saveContractorDraftEntry({
            contractorUserId: targetUserId,
            projectId,
            workDate: new Date(`${k}T12:00:00`),
            minutes,
            description: description.trim(),
            enteredByUserId: targetUserId !== user.id ? user.id : undefined,
          });
        }
        setDescription('');
        const skipped = dateKeys.length - toCreate.length;
        if (skipped > 0) {
          setMessage(
            `Logged ${toCreate.length} day${toCreate.length === 1 ? '' : 's'} (${skipped} already on this project skipped).`,
          );
        } else {
          setMessage(`Logged ${toCreate.length} day${toCreate.length === 1 ? '' : 's'}.`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save entries');
      } finally {
        setBusy(false);
      }
      return;
    }

    const date = new Date(`${workDate}T12:00:00`);
    minutes = parseHoursInput(hours);
    if (minutes <= 0) {
      setError('Enter a valid number of hours.');
      return;
    }
    if (wouldExceedContractorDailyCap(draftEntries, date, minutes)) {
      setError(formatContractorDailyCapError({
        workDate: date,
        existingMinutes: sumDraftMinutesForDate(draftEntries, workDate),
        additionalMinutes: minutes,
      }));
      return;
    }
    if (weekLocked) {
      setError(`${weekRangeLabel} has already been submitted.`);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await saveContractorDraftEntry({
        contractorUserId: targetUserId,
        projectId,
        workDate: date,
        minutes,
        description: description.trim(),
        enteredByUserId: targetUserId !== user.id ? user.id : undefined,
      });
      setDescription('');
      setMessage(logsByDay ? 'Day logged.' : 'Hours saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save entry');
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (entryId: string) => {
    setBusy(true);
    setError(null);
    try {
      await deleteContractorDraftEntry(entryId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitWeek = async () => {
    if (!targetUserId || !attest) return;
    setSubmitBusy(true);
    setError(null);
    setMessage(null);
    setSubmitResult(null);
    try {
      const parsedRate = parseFloat(submitRate);
      if (!Number.isFinite(parsedRate) || parsedRate <= 0) {
        setError(
          submitBillingDefaults.billingUnit === 'day'
            ? 'Enter a valid day rate before submitting.'
            : 'Enter a valid hourly rate before submitting.',
        );
        setSubmitBusy(false);
        return;
      }
      const result = await submitContractorTimesheet({
        contractorUserId: targetUserId !== user?.id ? targetUserId : undefined,
        periodStart: toDateInput(weekStart),
        periodEnd: toDateInput(weekEnd),
        billingUnit: submitBillingDefaults.billingUnit,
        rate: parsedRate,
      });
      setSubmitResult(result);
      setMessage(`Submitted — invoice ${result.invoiceNumber} ($${result.totalAmount.toFixed(2)})`);
      setAttest(false);
    } catch (err) {
      setError(formatCallableError(err));
    } finally {
      setSubmitBusy(false);
    }
  };

  const shiftWeek = useCallback((delta: number) => {
    setWeekStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + delta * 7);
      return startOfContractorWeek(d);
    });
  }, []);

  const contractorDisplayName = useMemo(() => {
    const match = crmUsers.find((u) => u.uid === targetUserId);
    return (
      match?.displayName
      || contractorOptions.find((u) => u.uid === targetUserId)?.displayName
      || 'the contractor'
    );
  }, [crmUsers, contractorOptions, targetUserId]);

  const isOnBehalfEntry = canOnBehalf && !!user?.id && targetUserId !== user.id;

  const attestationLabel = logsByDay
    ? isOnBehalfEntry
      ? `I attest on behalf of ${contractorDisplayName} that they performed the work described above during this period and the days logged are accurate.`
      : 'I attest that I performed the work described above during this period and the days logged are accurate.'
    : isOnBehalfEntry
      ? `I attest on behalf of ${contractorDisplayName} that they performed the work described above during this period and the hours are accurate.`
      : 'I attest that I performed the work described above during this period and the hours are accurate.';

  const weekBillableDays = useMemo(() => totalBillableDays(draftEntries), [draftEntries]);
  const parsedSubmitRate = parseFloat(submitRate);
  const submitRateOk = Number.isFinite(parsedSubmitRate) && parsedSubmitRate > 0;
  const estimatedInvoiceTotal =
    submitBillingDefaults.billingUnit === 'day'
      ? weekBillableDays * (submitRateOk ? parsedSubmitRate : 0)
      : (weekDraftMinutes / 60) * (submitRateOk ? parsedSubmitRate : 0);

  if (!user || !canAccess) {
    return <Navigate to="/" replace />;
  }

  if (profileLoading && !canOnBehalf) {
    return (
      <div className="ct-page">
        <TimesheetHeader />
        <LoadingScreen />
      </div>
    );
  }

  const hasPriorFiling = hasContractorPriorFiling({
    timesheetStatuses: timesheets.map((t) => t.status),
    entryStatuses: submittedEntries.map((e) => e.status),
  });
  const w9Ok = isContractorW9OnFile(profile) || hasPriorFiling;
  const dayRateRequired = contractorRequiresDayRateInvoicing(profile);
  const rateOk = profileRateConfiguredForSubmit(profile);
  const canSubmit =
    draftEntries.length > 0 && w9Ok && rateOk && submitRateOk && !weekLocked && attest;

  return (
    <div className="ct-page">
      <TimesheetHeader />
      <div className="ct-shell">
        <header className="ct-header">
          <div>
            <h1>Contractor Timesheets</h1>
            <p className="ct-subtitle">
              {logsByDay
                ? 'Log billable days by project — separate from W-2 time tracking.'
                : 'Log explicit hours by project — separate from W-2 time tracking.'}
            </p>
          </div>
          {canOnBehalf && (
            <div className="ct-on-behalf">
              <label htmlFor="ct-contractor-select">Contractor</label>
              <select
                id="ct-contractor-select"
                value={selectedContractorId}
                onChange={(e) => setSelectedContractorId(e.target.value)}
              >
                {contractorOptions.map((u) => (
                  <option key={u.uid} value={u.uid}>
                    {u.displayName}
                    {isCrmTestUser(u) ? ' (Test)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          {canViewPayables && (
            <Link to={buildContractorPayablesPath()} className="ct-payables-link">
              Contractor Payables
            </Link>
          )}
        </header>

        {portalPreview && (
          <div className="ct-banner ct-banner-preview" role="status">
            Staff preview — viewing <strong>{contractorDisplayName}</strong>
            {' '}
            contractor timesheets.
            {' '}
            <Link to="/external-portals">External portals</Link>
          </div>
        )}

        {dayRateRequired && rateOk && (
          <div className="ct-banner ct-banner-info" role="status">
            Use <strong>From / To</strong> to log multiple billable days on one project
            {profile?.defaultDayRate ? ` at $${profile.defaultDayRate}/day` : ''}
            . Different projects on the same dates are allowed.
          </div>
        )}

        {!rateOk && (
          <div className="ct-banner ct-banner-warn">
            {canManageContractorProfile && (canOnBehalf || portalPreview)
              ? dayRateRequired
                ? 'Day rate is not configured — set invoice billing and default day rate below.'
                : 'Hourly rate is not configured — set it in the contractor profile below.'
              : dayRateRequired
                ? 'Day rate is not configured on your profile. Ask Juliana or an admin to set it up before you submit.'
                : 'Hourly rate is not configured. Ask Juliana or an admin to set up your contractor profile.'}
          </div>
        )}

        {canManageContractorProfile && targetUserId && (canOnBehalf || portalPreview) && (
          <div className="ct-contractor-profile-setup">
            <ContractorProfileEditor
              key={targetUserId}
              userId={targetUserId}
              displayName={contractorDisplayName}
            />
          </div>
        )}
        {!w9Ok && (
          <div className="ct-banner ct-banner-warn">
            W-9 must be on file before you can submit a timesheet.
          </div>
        )}

        <div className="ct-week-bar">
          <button type="button" className="ct-week-btn" onClick={() => shiftWeek(-1)} aria-label="Previous week">
            <ChevronLeft size={18} />
          </button>
          <span className="ct-week-label">
            {weekRangeLabel}
            {isSelectedCurrentWeek ? ' · current week' : ''}
          </span>
          <button type="button" className="ct-week-btn" onClick={() => shiftWeek(1)} aria-label="Next week">
            <ChevronRight size={18} />
          </button>
          {weekTimesheet && (
            <span className={`ct-week-status ct-status-${weekTimesheet.status}`}>
              {TIMESHEET_STATUS_LABELS[weekTimesheet.status]}
            </span>
          )}
        </div>

        <div className="ct-grid">
          <section className="ct-panel">
            <h2>{logsByDay ? 'Log days' : 'Log hours'}</h2>
            <div className="ct-field">
              <label>Project (required)</label>
              <ContractorProjectPicker
                value={projectId}
                onChange={setProjectId}
                options={projectOptions}
                suggestedIds={recentProjectIds}
                disabled={weekLocked}
              />
            </div>
            <div className="ct-field-row">
              {logsByDay ? (
                <>
                  <div className="ct-field">
                    <label htmlFor="ct-date-from">From</label>
                    <input
                      id="ct-date-from"
                      type="date"
                      value={workDateFrom}
                      min={toDateInput(weekStart)}
                      max={toDateInput(weekEnd)}
                      onChange={(e) => setWorkDateFrom(e.target.value)}
                      disabled={weekLocked}
                    />
                  </div>
                  <div className="ct-field">
                    <label htmlFor="ct-date-to">To</label>
                    <input
                      id="ct-date-to"
                      type="date"
                      value={workDateTo}
                      min={toDateInput(weekStart)}
                      max={toDateInput(weekEnd)}
                      onChange={(e) => setWorkDateTo(e.target.value)}
                      disabled={weekLocked}
                    />
                    <p className="ct-field-hint">
                      One billable day per date in the range on this project. Dates already logged are skipped.
                    </p>
                  </div>
                </>
              ) : (
              <div className="ct-field">
                <label htmlFor="ct-date">Date</label>
                <input
                  id="ct-date"
                  type="date"
                  value={workDate}
                  min={toDateInput(weekStart)}
                  max={toDateInput(weekEnd)}
                  onChange={(e) => setWorkDate(e.target.value)}
                  disabled={weekLocked}
                />
              </div>
              )}
              {!logsByDay && (
                <div className="ct-field">
                  <label htmlFor="ct-hours">Hours</label>
                  <input
                    id="ct-hours"
                    type="number"
                    min={0.25}
                    max={maxHoursForSelectedDay}
                    step={0.25}
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    disabled={weekLocked}
                  />
                  <p className="ct-field-hint">
                    Up to {formatHours(remainingMinutesForSelectedDay)}h remaining on this date
                    ({CONTRACTOR_MAX_DAILY_HOURS}h daily max).
                  </p>
                </div>
              )}
            </div>
            <div className="ct-field">
              <label htmlFor="ct-desc">Work description (required)</label>
              <textarea
                id="ct-desc"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What did you work on?"
                disabled={weekLocked}
              />
              {projectId === CONTRACTOR_OTHER_PROJECT_ID && (
                <p className="ct-field-hint">
                  Describe the work — required for Other entries (overhead, admin, etc.).
                </p>
              )}
            </div>
            <button
              type="button"
              className="ct-primary-btn"
              onClick={addEntry}
              disabled={busy || weekLocked}
            >
              <Plus size={16} />
              {busy ? 'Saving…' : logsByDay ? 'Log days' : 'Add entry'}
            </button>
          </section>

          <section className="ct-panel ct-entries-panel">
            <div className="ct-entries-head">
              <div className="ct-entries-head-text">
                <h2>{weekRangeLabel}</h2>
                <p className="ct-entries-head-sub">
                  {isSelectedCurrentWeek ? 'Current pay week' : 'Selected pay week'}
                </p>
              </div>
              <span className={entriesSummary.statusClass}>{entriesSummary.label}</span>
            </div>
            {entries.length === 0 ? (
              <p className="ct-empty">No entries for {weekRangeLabel} yet.</p>
            ) : (
              <ul className="ct-entry-list">
                {entries.map((e) => {
                  const projectLabel = contractorProjectEntryLabel(e.projectId, projectOptions);
                  return (
                    <li key={e.id} className={`ct-entry ct-entry-${e.status}`}>
                      <div className="ct-entry-main">
                        <span className="ct-entry-date">
                          {e.workDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="ct-entry-hours">{formatContractorEntryQuantity(e.minutes, logsByDay)}</span>
                        {e.status !== 'draft' && (
                          <span className={`ct-entry-status ct-entry-status-${e.status}`}>
                            {ENTRY_STATUS_LABELS[e.status]}
                          </span>
                        )}
                        <span className="ct-entry-project">{projectLabel}</span>
                        <p className="ct-entry-desc">{e.description}</p>
                        {e.enteredByUserId && e.enteredByUserId !== e.contractorUserId && (
                          <span className="ct-entry-onbehalf">Entered on behalf</span>
                        )}
                      </div>
                      {e.status === 'draft' && e.id && (
                        <button
                          type="button"
                          className="ct-icon-btn"
                          onClick={() => removeEntry(e.id!)}
                          disabled={busy}
                          aria-label="Delete entry"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {!weekLocked && draftEntries.length > 0 && (
              <div className="ct-submit-block">
                <label className="ct-attest">
                  <input
                    type="checkbox"
                    checked={attest}
                    onChange={(e) => setAttest(e.target.checked)}
                  />
                  {attestationLabel}
                </label>
                <div className="ct-submit-billing">
                  {submitBillingDefaults.billingUnit === 'day' ? (
                    <p className="ct-submit-billing-meta">
                      Billable days this week: <strong>{weekBillableDays}</strong>
                    </p>
                  ) : (
                    <p className="ct-submit-billing-meta">
                      Hours this week: <strong>{formatHours(weekDraftMinutes)}</strong>
                    </p>
                  )}
                  <label className="ct-submit-rate-label" htmlFor="ct-submit-rate">
                    {submitBillingDefaults.billingUnit === 'day' ? 'Day rate ($)' : 'Hourly rate ($)'}
                  </label>
                  <input
                    id="ct-submit-rate"
                    type="number"
                    min={0.01}
                    step={0.01}
                    className="ct-submit-rate-input"
                    value={submitRate}
                    onChange={(e) => setSubmitRate(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="ct-submit-btn"
                  onClick={handleSubmitWeek}
                  disabled={!canSubmit || submitBusy}
                >
                  <Send size={16} />
                  {submitBusy ? 'Submitting…' : 'Submit timesheet for payment'}
                </button>
                {profile && rateOk && submitRateOk && (
                  <p className="ct-estimate">
                    Estimated invoice: ${estimatedInvoiceTotal.toFixed(2)}
                    {submitBillingDefaults.billingUnit === 'day'
                      ? ` (${weekBillableDays} day${weekBillableDays === 1 ? '' : 's'} × $${parsedSubmitRate}/day)`
                      : ` (${formatHours(weekDraftMinutes)}h × $${parsedSubmitRate}/hr)`}
                  </p>
                )}
              </div>
            )}
          </section>
        </div>

        {timesheets.length > 0 && (
          <section className="ct-panel ct-history">
            <h2>Submission history</h2>
            <table className="ct-history-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Jobs</th>
                  <th>Submitted</th>
                  <th>Hours</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {timesheets.slice(0, 12).map((t) => {
                  const dayBreakdown = t.id ? dayBreakdownByTimesheet.get(t.id) : undefined;
                  const jobRows = resolveTimesheetJobs(t, jobBreakdownByTimesheet);
                  return (
                  <tr key={t.id} className="ct-history-row">
                    <td>{formatWeekRange(t.periodStart)}</td>
                    <td className="ct-history-jobs">
                      {jobRows.length === 0 ? (
                        '—'
                      ) : (
                        <ul className="ct-history-jobs-list">
                          {jobRows.map((row) => (
                            <li key={row.projectId}>
                              {jobRows.length > 1 && row.minutes > 0 && (
                                <span className="ct-history-job-hours">
                                  {formatContractorBreakdownHours(row.minutes)}h
                                </span>
                              )}
                              {contractorProjectEntryLabel(row.projectId, projectOptions)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="ct-history-submitted">{formatContractorSubmittedAt(t.submittedAt)}</td>
                    <td className="ct-history-hours-cell">
                      <span
                        className={`ct-history-hours-trigger${dayBreakdown?.length ? ' ct-history-hours-trigger--has-tip' : ''}`}
                        tabIndex={dayBreakdown?.length ? 0 : undefined}
                      >
                        {formatHours(t.totalMinutes)}h
                        {dayBreakdown && dayBreakdown.length > 0 && (
                          <span className="ct-history-breakdown-tooltip" role="tooltip">
                            <span className="ct-history-breakdown-title">Daily breakdown</span>
                            <ul className="ct-history-breakdown-list">
                              {dayBreakdown.map((row) => (
                                <li key={row.dateKey}>
                                  {formatContractorBreakdownHours(row.minutes)} hrs {row.label}
                                </li>
                              ))}
                            </ul>
                          </span>
                        )}
                      </span>
                    </td>
                    <td>${t.totalAmount.toFixed(2)}</td>
                    <td>
                      <span className={`ct-week-status ct-status-${t.status}`}>
                        {TIMESHEET_STATUS_LABELS[t.status]}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        {error && <div className="ct-error">{error}</div>}
        {message && (
          <div className="ct-success">
            <p>{message}</p>
            {submitResult && canViewPayables && (
              <Link
                to={buildContractorPayablesPath(submitResult.invoiceId)}
                className="ct-success-link"
              >
                Open invoice in Contractor Payables
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractorTimesheetsPage;
